/**
 * Footer Component
 *
 * Footer igual ao PHP com:
 * - "powered by trademarketingforce" (esquerda)
 * - "Política de Privacidade" (centro)
 * - "Versão: X / Database Switcher" (direita)
 *
 * @since 2025-12-16
 * @updated 2026-01-09 - Added DatabaseSwitcher
 * @epic INIT-0046
 */

import { useState, useEffect, useRef } from 'react';
import { FileText } from 'lucide-react';
import { useTheme } from '@/theme';
import { DatabaseSwitcher } from './DatabaseSwitcher';
import { DevResetButton } from '@/components/dev/DevResetButton';
import { ChangelogModal } from './ChangelogModal';
import { useChangelog } from '@/hooks/useChangelog';
import { useAuth } from '@/contexts/AuthContext';

const CHANGELOG_STORAGE_KEY = 'tmf9_lastSeenChangelogVersion';
const AUTO_OPEN_ROLES = ['admin', 'superadmin', 'ADMIN', 'SUPERADMIN', 'administrator', 'Sadin', 'sadin'];

// Versão exibida no footer = versão do frontend (package.json), injetada
// pelo Vite via `define.__APP_VERSION__`. Garante que o número aqui bata
// com o `changelog[0].version` mostrado no modal "O que há de novo".
// Antes vinha do backend (`/api/v1/core/health`) e divergia quando só
// o frontend era bumpado entre releases.
const APP_VERSION =
  typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '';

export function Footer() {
  const { theme } = useTheme();
  const { user, isAuthenticated } = useAuth();
  const { changelog } = useChangelog();
  const [showChangelog, setShowChangelog] = useState(false);
  const autoOpenChecked = useRef(false);

  // Auto-open changelog para admin/superadmin se há versão nova
  useEffect(() => {
    if (autoOpenChecked.current || !isAuthenticated || !user?.roles) return;

    const latestVersion = changelog[0]?.version;
    if (!latestVersion) return; // changelog ainda carregando (fetch async)
    autoOpenChecked.current = true;

    const hasAdminRole = user.roles.some((role: string) => AUTO_OPEN_ROLES.includes(role));
    if (!hasAdminRole) return;

    const lastSeen = localStorage.getItem(CHANGELOG_STORAGE_KEY);
    if (lastSeen !== latestVersion) {
      setShowChangelog(true);
    }
  }, [isAuthenticated, user, changelog]);

  const handleCloseChangelog = () => {
    setShowChangelog(false);
    const latestVersion = changelog[0]?.version;
    if (latestVersion) {
      localStorage.setItem(CHANGELOG_STORAGE_KEY, latestVersion);
    }
  };

  return (
    <>
      <footer
        className="py-3 px-4"
        style={{
          backgroundColor: 'transparent',
          background: 'none',
          boxShadow: 'none',
        }}
      >
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          {/* Left: powered */}
          <div className="flex items-center gap-1.5">
            <img
              src="/images/logos/logo.png"
              alt="Trade Marketing Force"
              className="h-6 w-6 rounded"
            />
          </div>

          {/* Center: Privacy Policy */}
          <a
            href="/politica-de-privacidade"
            className="hover:opacity-80 transition"
          >
            Política de Privacidade
          </a>

          {/* Right: Version + Changelog + Database Switcher + Dev Reset */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowChangelog(true)}
              className="flex items-center gap-1.5 text-foreground/80 hover:text-foreground transition-colors"
              title="Ver changelog"
            >
              <span>Versão: {APP_VERSION || '/'}</span>
              <FileText className="w-3.5 h-3.5" />
            </button>
            <DatabaseSwitcher />
            <DevResetButton />
          </div>
        </div>
      </footer>

      <ChangelogModal isOpen={showChangelog} onClose={handleCloseChangelog} />
    </>
  );
}

export default Footer;
